import { useMemo } from "react";

// Branch-management role detection, mirroring the backend rules in
// employee/permissions.py. Reads session state populated by AuthContext.
//
// Role ladder (highest -> lowest):
//   super_admin     -> is_admin / is_super_admin  (org user; all branches)
//   branch_admin    -> is_branch_admin            (own branch only)
//   employee        -> everyone else (read-only unless delegated can_*)
//
// This hook decides what the UI SHOWS. The backend still enforces every rule,
// so a tampered client can never exceed these — the hook is UX, not security.
const readBool = (key) => {
    const v = sessionStorage.getItem(key);
    return v === "true" || v === true;
};

const useBranchRole = () => {
    return useMemo(() => {
        const isSuperAdmin = readBool("is_super_admin") || readBool("is_admin");
        const isBranchManager = readBool("is_branch_admin");
        const role = isSuperAdmin ? "super_admin" : isBranchManager ? "branch_manager" : "employee";

        // Delegated CRUD (only meaningful for a plain employee; managers/admins
        // get their rights from the role itself).
        const canCreate = isSuperAdmin || isBranchManager || readBool("can_create");
        const canEdit = isSuperAdmin || isBranchManager || readBool("can_edit");
        const canDelete = isSuperAdmin || isBranchManager || readBool("can_delete");

        const branchId = sessionStorage.getItem("branch_id");
        const branchName = sessionStorage.getItem("branch_name") || "";
        const branchAvail = readBool("branch_avail");

        return {
            role,
            isSuperAdmin,
            isBranchManager,
            isEmployee: role === "employee",
            branchId: branchId && branchId !== "null" ? branchId : null,
            branchName,
            branchAvail,

            // --- capability flags for gating UI ---
            // Branch CRUD screen: only super admin / org admin manages branches.
            canManageBranches: isSuperAdmin,
            // Employee record actions.
            canCreateEmployee: canCreate,
            canEditEmployee: canEdit,
            canDeleteEmployee: canDelete,

            // Role promotion the current user may offer in an employee form:
            //   super admin  -> Employee / Branch Manager / Super Admin
            //   branch mgr   -> up to Branch Manager (own branch)
            //   employee     -> none
            assignableRoles: isSuperAdmin
                ? ["employee", "branch_manager", "super_admin"]
                : isBranchManager
                    ? ["employee", "branch_manager"]
                    : [],

            // Whether the "grant create/edit/delete" checkboxes should show.
            canDelegateAccess: isSuperAdmin || isBranchManager,
        };
    }, []);
};

export default useBranchRole;
